import {
  MAPBOX_ACCESS_TOKEN,
  MAPBOX_OPERATOR_SEARCH_STYLE_URL,
  type BuxusOperatorPayload,
} from '@buxus/shared'
import type { ScenarioMapKind, ScenarioMapRef } from './types'

const MAP_KINDS = new Set<ScenarioMapKind>(['operator', 'all-operators', 'basemap', 'fictional'])

/** Minimal catalog DTO shape (avoids importing the API module from here). */
export type ScenarioMapCatalogRow = {
  mapKey?: string | null
  title?: string | null
  kind?: string | null
  mapboxStyleUrl?: string | null
  mapboxAccessTokenOverride?: string | null
  isActive?: boolean
}

/** Active catalog row → picker ref (null when inactive / incomplete). */
export function mapRefFromServerMap(row: ScenarioMapCatalogRow): ScenarioMapRef | null {
  if (row.isActive === false) return null
  const id = row.mapKey?.trim()
  const styleUrl = row.mapboxStyleUrl?.trim()
  const kind = row.kind?.trim() as ScenarioMapKind | undefined
  if (!id || !styleUrl || !kind || !MAP_KINDS.has(kind)) return null
  return {
    id,
    kind,
    label: row.title?.trim() || id,
    styleUrl,
  }
}

/** Eagle Mountain operator style used by the original HTML POC. */
export const EAGLE_MOUNTAIN_STYLE_URL =
  'mapbox://styles/sanderresources/ckrpdzrnx2lat18nwk3vd206r'

/**
 * Client stand-in for GET /api/scenarios/maps. Ids match ScenarioMaps.mapKey.
 * Templates copy a curated subset into session.maps (see DEMO_ / EAGLE_BOARD_MAP_IDS).
 */
const MAP_PICKER_ORDER = [
  'streets',
  'satellite',
  'bare-geography',
  'eagle-mountain',
  'all-operators',
] as const

/** Sander “Scenario Studio Streets” — keep in sync with ScenarioMaps/streets.json. */
export const STREETS_STYLE_URL =
  'mapbox://styles/sanderresources/cmvueshwu003s01s25ksqdh4g'

/** Sander “Scenario Studio Satellite” — keep in sync with ScenarioMaps/satellite.json. */
export const SATELLITE_STYLE_URL =
  'mapbox://styles/sanderresources/cmvuf10r9003u01p4czvqds8l'

export const BARE_GEOGRAPHY_STYLE_URL =
  'mapbox://styles/sanderresources/cmvueuor2003l01qpanq9dpqq'

export const SCENARIO_MAP_CATALOG: readonly ScenarioMapRef[] = [
  {
    id: 'streets',
    kind: 'basemap',
    label: 'Streets',
    styleUrl: STREETS_STYLE_URL,
  },
  {
    id: 'satellite',
    kind: 'basemap',
    label: 'Satellite',
    styleUrl: SATELLITE_STYLE_URL,
  },
  {
    id: 'bare-geography',
    kind: 'basemap',
    label: 'Bare geography',
    styleUrl: BARE_GEOGRAPHY_STYLE_URL,
  },
  {
    id: 'eagle-mountain',
    kind: 'operator',
    label: 'Eagle Mountain',
    styleUrl: EAGLE_MOUNTAIN_STYLE_URL,
  },
  {
    id: 'all-operators',
    kind: 'all-operators',
    label: 'All Operators',
    styleUrl: MAPBOX_OPERATOR_SEARCH_STYLE_URL,
  },
]

/** Blanks / teaching demos — shared basemaps plus the Eagle Mountain plate. */
export const DEMO_BOARD_MAP_IDS = [
  'streets',
  'satellite',
  'bare-geography',
  'eagle-mountain',
  'all-operators',
] as const

/** Eagle Mountain area boards — include the operator style. */
export const EAGLE_BOARD_MAP_IDS = [
  'streets',
  'satellite',
  'bare-geography',
  'eagle-mountain',
  'all-operators',
] as const

/** Build session.maps from catalog ids (picker order preserved by mapsOffered sort). */
export function mapsFromCatalog(ids: readonly string[]): ScenarioMapRef[] {
  const byId = new Map(SCENARIO_MAP_CATALOG.map((m) => [m.id, m]))
  return ids.map((id) => {
    const row = byId.get(id)
    if (!row) throw new Error(`Unknown scenario map id: ${id}`)
    return { ...row }
  })
}

/** Same plate as catalog `streets` — kept for poster/wizard call sites. */
export const MAP_POSTER_STYLE_ID = 'streets' as const
export const MAP_POSTER_PLATE_STYLE_URL = STREETS_STYLE_URL
export const MAP_POSTER_PLATE_REF: ScenarioMapRef = SCENARIO_MAP_CATALOG[0]!

/** No-op when the plate lives in the catalog; kept for existing call sites. */
export function ensurePosterPlateMapRegistered(): void {
  /* catalog row is enough for mapRefById */
}

/** Public pk token for styles on the Sander Mapbox account. */
export function defaultMapboxToken(): string {
  const fromEnv = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  if (typeof fromEnv === 'string' && fromEnv.trim()) return fromEnv.trim()
  return MAPBOX_ACCESS_TOKEN
}

const tokenOverrides = new Map<string, string>()

/** Runtime maps not in the static catalog (e.g. downloaded operator styles). */
const extraMaps = new Map<string, ScenarioMapRef>()

function catalogPlate(id: string): ScenarioMapRef {
  return SCENARIO_MAP_CATALOG.find((m) => m.id === id) ?? SCENARIO_MAP_CATALOG[0]!
}

/**
 * Style URL from the session plate, plus the token that can load that URL.
 * A catalog token is used only when that row's style URL is the one being loaded.
 */
export function sessionMapStyle(
  maps: readonly ScenarioMapRef[] | undefined,
  mapId: string,
  catalog?: readonly ScenarioMapCatalogRow[],
): { styleUrl: string; accessToken: string } {
  const id = mapId.trim()
  const plate = maps?.find((m) => m.id === id && m.styleUrl.trim())
  const styleUrl = plate?.styleUrl.trim() || catalogPlate(id).styleUrl
  return { styleUrl, accessToken: tokenForStyle(catalog, id, styleUrl) }
}

function tokenForStyle(
  catalog: readonly ScenarioMapCatalogRow[] | undefined,
  mapId: string,
  styleUrl: string,
): string {
  const sameId = catalog?.find((row) => row.mapKey?.trim() === mapId)
  const sameIdUrl = sameId?.mapboxStyleUrl?.trim()
  const sameIdToken = sameId?.mapboxAccessTokenOverride?.trim()
  if (sameIdToken && (!sameIdUrl || sameIdUrl === styleUrl)) return sameIdToken
  const sameUrl = catalog?.find(
    (row) =>
      row.mapboxStyleUrl?.trim() === styleUrl && row.mapboxAccessTokenOverride?.trim(),
  )
  return sameUrl?.mapboxAccessTokenOverride?.trim() || defaultMapboxToken()
}

/** Keep a hub `maps` payload that is a list of plates. Anything else is ignored. */
export function coerceSessionMaps(raw: unknown): ScenarioMapRef[] | null {
  if (!Array.isArray(raw)) return null
  const plates: ScenarioMapRef[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const row = item as Partial<ScenarioMapRef>
    const id = typeof row.id === 'string' ? row.id.trim() : ''
    const styleUrl = typeof row.styleUrl === 'string' ? row.styleUrl.trim() : ''
    const label = typeof row.label === 'string' ? row.label.trim() : ''
    const kind = row.kind
    if (!id || !styleUrl || !label || !kind || !MAP_KINDS.has(kind)) continue
    plates.push({ id, kind, label, styleUrl })
  }
  return plates.length ? plates : null
}

/** Remember per-map pk tokens from GET /api/scenarios/maps. Empty values clear that key. */
export function rememberMapboxTokenOverrides(
  rows: readonly { mapKey?: string | null; mapboxAccessTokenOverride?: string | null }[],
): void {
  tokenOverrides.clear()
  for (const row of rows) {
    const key = row.mapKey?.trim()
    const token = row.mapboxAccessTokenOverride?.trim()
    if (key && token) tokenOverrides.set(key, token)
  }
}

/** Override for this catalog key, otherwise the Sander token. */
export function mapboxTokenFor(mapId: string): string {
  return tokenOverrides.get(mapId) ?? defaultMapboxToken()
}

/** Register or refresh client-only map refs (downloaded operators). */
export function rememberExtraMaps(rows: readonly ScenarioMapRef[]): void {
  for (const row of rows) {
    if (!row.id?.trim() || !row.styleUrl?.trim()) continue
    extraMaps.set(row.id, { ...row })
  }
}

export function operatorMapId(operatorId: string): string {
  return `op:${operatorId.trim()}`
}

function isLikelyMapboxStyleUrl(v: string): boolean {
  const s = v.trim()
  return s.startsWith('mapbox://styles/') || s.startsWith('https://')
}

function readOperatorField(operator: Record<string, unknown> | undefined, key: string): unknown {
  if (!operator) return undefined
  return operator[key]
}

/** Style URL from a downloaded Buxus operator payload (`mapboxLink` / `mapStyleUrl`). */
export function styleUrlFromOperatorPayload(payload: BuxusOperatorPayload | null | undefined): string | null {
  if (!payload) return null
  const operator = (payload.operator ?? (payload as unknown as Record<string, unknown>).Operator) as
    | Record<string, unknown>
    | undefined
  const raw =
    readOperatorField(operator, 'mapboxLink') ??
    readOperatorField(operator, 'MapboxLink') ??
    readOperatorField(operator, 'mapStyleUrl') ??
    readOperatorField(operator, 'MapStyleUrl')
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed || !isLikelyMapboxStyleUrl(trimmed)) return null
  return trimmed
}

export function mapRefFromDownloadedOperator(
  operatorId: string,
  name: string,
  payload: BuxusOperatorPayload | null | undefined,
): ScenarioMapRef | null {
  const styleUrl = styleUrlFromOperatorPayload(payload)
  if (!styleUrl) return null
  const id = operatorMapId(operatorId)
  const label = name.trim() || 'Operator map'
  return { id, kind: 'operator', label, styleUrl }
}

export function mapRefById(id: string): ScenarioMapRef {
  return (
    extraMaps.get(id) ??
    SCENARIO_MAP_CATALOG.find((m) => m.id === id) ??
    SCENARIO_MAP_CATALOG.find((m) => m.id === 'eagle-mountain') ??
    SCENARIO_MAP_CATALOG[0]!
  )
}

function mapPickerRank(id: string): number {
  const index = MAP_PICKER_ORDER.indexOf(id as (typeof MAP_PICKER_ORDER)[number])
  return index === -1 ? MAP_PICKER_ORDER.length : index
}

export function isDownloadedOperatorMapId(id: string): boolean {
  return id.startsWith('op:')
}

/** All Operators + downloaded op:* plates — under the Operator Maps divider. */
export function isOperatorPickerMap(m: ScenarioMapRef): boolean {
  return m.id === 'all-operators' || isDownloadedOperatorMapId(m.id)
}

function sortOfferedMaps(rows: ScenarioMapRef[]): ScenarioMapRef[] {
  return rows.sort(
    (a, b) =>
      mapPickerRank(a.id) - mapPickerRank(b.id) ||
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  )
}

/** Maps this board offers, in picker order. Empty boards get the demo set (incl. Eagle Mountain). */
export function mapsOffered(maps: readonly ScenarioMapRef[] | undefined): ScenarioMapRef[] {
  const listed = maps?.length ? maps : mapsFromCatalog(DEMO_BOARD_MAP_IDS)
  return sortOfferedMaps(
    listed.filter((m) => m.id !== 'no-map').map((m) => ({ ...m })),
  )
}

export type MapPickerSections = {
  /** Shared basemaps + special plates (e.g. Eagle Mountain on Eagle boards). */
  board: ScenarioMapRef[]
  /** All Operators + downloaded op:* styles. */
  operators: ScenarioMapRef[]
}

/** Native <option> labels don’t ellipsize well — truncate for the picker. */
export function truncateMapPickerLabel(label: string, maxChars = 40): string {
  const t = label.trim()
  if (t.length <= maxChars) return t
  return `${t.slice(0, Math.max(1, maxChars - 1))}…`
}

/**
 * Background Map picker sections:
 * 1) board maps — Streets / Satellite / Bare geography (+ Eagle Mountain when offered)
 * 2) Operator Maps — All Operators + downloaded op:* styles
 *
 * Does not mutate the session until the user picks one (then persist that ref).
 */
export function mapsForPicker(
  sessionMaps: readonly ScenarioMapRef[] | undefined,
  downloadedOperatorMaps: readonly ScenarioMapRef[],
): MapPickerSections {
  const offered = mapsOffered(sessionMaps)
  const board = offered.filter((m) => !isOperatorPickerMap(m))
  const eagle = SCENARIO_MAP_CATALOG.find((m) => m.id === 'eagle-mountain')
  if (eagle && !board.some((m) => m.id === eagle.id)) {
    board.push({ ...eagle })
    sortOfferedMaps(board)
  }

  const opById = new Map<string, ScenarioMapRef>()
  for (const m of offered) {
    if (!isOperatorPickerMap(m) || !m.styleUrl?.trim()) continue
    opById.set(m.id, { ...m })
  }
  // Fresh download wins label + styleUrl for the same id (heal drift).
  for (const m of downloadedOperatorMaps) {
    if (!m.id?.trim() || !m.styleUrl?.trim()) continue
    opById.set(m.id, { ...m })
  }

  const operators = [...opById.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  )
  return { board, operators }
}
